import * as React from 'react'
import I18n from '../../../../core/i18n'
import styled from 'styled-components'
import { uniqueId } from 'lodash'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService } from '../../../../core/services'
import { InputText, Button } from '../../../atoms'
import AgentLayoutEdit from '../_layouts/edit'
import * as interfaces from '../../../../static/interfaces'
import SidebarLayout from 'components/pages/_layouts/sidebarLayout'

declare var window: interfaces.IWindow

interface IProps {
  cancel_policy: IJsonResponse
  user: IJsonResponse
}

const FIELDS = {
  id: 'id',
  name: 'name',
  fee_percentage: 'fee_percentage',
}

const title = I18n.t('admin.edit', {
  model: I18n.t('cancel_policy', { scope: 'activerecord.models' }),
})

const newRateField = {
  id: null,
  percentage: 0,
  until_days_before: 0,
}

const CancelPolicyEdit: React.FC<IProps> = props => {
  const { data: cancelPolicy } = agentService.getDataFromJson(props.cancel_policy)
  const { data: user } = agentService.getDataFromJson(props.user)
  const [rates, setRates] = React.useState(cancelPolicy.cancel_policy_rates || [newRateField])
  const handleSubmit = React.useCallback(
    async (_initialValues, values) => {
      const params = { cancel_policy_attributes: values, cancel_policy_rates_attributes: rates }
      try {
        const { flush } = await agentService.updateCancelPolicy(params)
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      } catch (e) {
        console.error(e)
      }
    },
    [rates]
  )
  const addRateField = React.useCallback(() => {
    setRates(rates.concat([{ ...newRateField, key: uniqueId() }]))
  }, [rates])
  const removeRateField = React.useCallback(
    index => {
      setRates(rates.filter((rate, i) => index !== i))
    },
    [rates]
  )
  const handleChange = React.useCallback(
    (e, index) => {
      setRates(
        rates.map((rate, i) => (index === i ? { ...rate, [e.target.name]: e.target.value } : rate))
      )
    },
    [rates]
  )

  return (
    <SidebarLayout user={user} showSidebar={true}>
      <AgentLayoutEdit
        model="cancel_policies"
        hideAgentNav={true}
        title={title}
        fields={FIELDS}
        indexLink="/agent/cancel_policies"
        formItems={
          <Wrapper>
            <InputText
              readonly={true}
              name="id"
              defaultValue={cancelPolicy.id}
              label={I18n.t('generic.id')}
            />
            <InputText
              required={true}
              name="name"
              defaultValue={cancelPolicy.name}
              label={I18n.t('generic.name')}
            />
            <div className="feePercentage">
              <InputText
                required={false}
                name="fee_percentage"
                defaultValue={cancelPolicy.fee_percentage}
                label={I18n.t('fee_percentage', { scope: 'activerecord.attributes.cancel_policy' })}
              />
              <div>%</div>
            </div>
            <div className="rateArea">
              <div className="title">
                {I18n.t('percentage', { scope: 'activerecord.attributes.cancel_policy_rate' })}
              </div>
              <div className="rates">
                {rates.map((rate, index) => (
                  <div className="rate" key={rate.id || rate.key}>
                    <div className="rateItem">
                      <InputText
                        required={true}
                        name="until_days_before"
                        defaultValue={rate.until_days_before}
                        onChangeHandler={e => handleChange(e, index)}
                      />
                      <div>日前までは</div>
                    </div>
                    <div className="rateItem">
                      <InputText
                        required={true}
                        name="percentage"
                        defaultValue={rate.percentage}
                        onChangeHandler={e => handleChange(e, index)}
                      />
                      <div>%</div>
                    </div>
                    <div className="deleteButton">
                      <Button
                        handleClick={() => removeRateField(index)}
                        backgroundColor="red"
                        textColor="white"
                        small={true}
                      >
                        削除
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="addButton">
                  <Button handleClick={addRateField} small={true}>
                    追加
                  </Button>
                </div>
              </div>
            </div>
          </Wrapper>
        }
        handleSubmit={handleSubmit}
      />
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  .feePercentage {
    display: flex;
    align-items: center;
    margin-top: 24px;
    gap: 10px;
  }
  .rateArea {
    display: flex;
    align-items: baseline;
    margin-top: 14px;
    .title {
      width: 180px;
      font-size: 14px;
      font-weight: bold;
    }
    .rates {
      flex: 1;
      .rate {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-top: 10px;
        .rateItem {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          .InputText {
            width: 60%;
          }
        }
      }
      .addButton {
        margin-top: 30px;
        text-align: center;
        button {
          width: 100%;
        }
      }
    }
  }
`
export default CancelPolicyEdit
